import { Injectable } from '@angular/core';
import { BaseAPI } from '@core/services/base-api.service';
import { Mapper } from '@core/services/base-mapper.service';
import { filterNullEntity } from '@shared/utils';
import { APIConstants } from '@shared/constants';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { DateRangeType, SortDirection } from '@shared-features/enums/shared-enums.enum';
import { OrderLineStatus } from '../../shared-features/enums/order-line-status.enum';
import { Order } from '../models/order.model';
import { Pallet } from '../models/pallet.model';
import { OrdersStatusCount } from '../models/orders-status-count.model';
import { OrderLineItem } from '@features/orders/models/order-line-item.model';
import { UpdateOrdersPriorityDto } from '../dto/update-orders-priority.dto';

@Injectable({
  providedIn: 'root',
})
export class OrderAPIService {
  constructor(private baseAPI: BaseAPI, private mapper: Mapper) {}

  getOrderList(payload: {
    offset?: number;
    limit?: number;
    searchTerm?: string;
    status?: OrderLineStatus;
    dateRangeType: DateRangeType;
    startFrom?: string;
    endAt?: string;
    sortDirection?: SortDirection;
    sortField?: any;
    IsForCreation?: boolean;
    customerIds?: number[];
    shipmentId?: number;
    orderType?: string[];
    temperatureControl?: string;
    isRush?: boolean;
    withoutPagination?: boolean;
    isDetailed?: boolean;
  }): Observable<Order[]> {
    const { customerIds, orderType, ...resetOfPayload } = payload;
    // Adding brackets array in the key to make the single value as array
    const params = { ...resetOfPayload, 'customerIds[]': payload.customerIds, 'orderType[]': payload.orderType };
    return this.baseAPI.get(APIConstants.API_GET_ORDER_LIST, { params: filterNullEntity(params) }).pipe(
      map((res) => {
        return this.mapper.fromJson(Order, res.data);
      })
    );
  }

  getOrderListById(shipmentId) {
    return this.baseAPI.get(APIConstants.API_GET_ORDER_LIST + '/' + shipmentId).pipe(
      map((res) => {
        return this.mapper.fromJson(Order, res.data);
      })
    );
  }

  getPalletList(payload: {
    orderId?: number;
    isForCreation?: boolean;
    shipmentId?: number;
    searchTerm?: string;
    orderStatus?: string;
    limit?: number;
    offset?: number;
  }): Observable<Pallet[]> {
    return this.baseAPI.get(APIConstants.API_GET_PALLET_LIST, { params: filterNullEntity(payload) }).pipe(
      map((res) => {
        return this.mapper.fromJson(Pallet, res.data);
      })
    );
  }

  getOrdersCount(payload?: {
    searchTerm?: string;
    dateRangeType: DateRangeType;
    startFrom?: string;
    endAt?: string;
    IsForCreation?: boolean;
    customerIds?: number[];
    orderType?: string[];
    temperatureControl?: string;
    isRush?: boolean;
    isDetailed?: boolean;
  }): Observable<OrdersStatusCount> {
    const { customerIds, orderType, ...resetOfPayload } = payload;
    const params = { ...resetOfPayload, 'customerIds[]': payload.customerIds, 'orderType[]': payload.orderType };

    return this.baseAPI.get(APIConstants.API_GET_ORDERS_COUNT, { params: filterNullEntity(params) }).pipe(
      map((res) => {
        return this.mapper.fromJson(OrdersStatusCount, res.data);
      })
    );
  }

  getUndetailedOrderInfo(payload: { orderId: number }): Observable<OrderLineItem[]> {
    return this.baseAPI.get(APIConstants.API_GET_UNDETAILED_ORDER_INFO + '/' + payload.orderId).pipe(
      map((res) => {
        return res.data;
      })
    );
  }

  checkIfOrderDetailed(): Observable<boolean> {
    return this.getOrderList({ limit: 1, offset: 0, dateRangeType: DateRangeType.ALL_TIME }).pipe(
      map((orders) => {
        const order = orders[0];
        return order && order.isPalletDetails;
      })
    );
  }

  updateOrdersPriority(payload: UpdateOrdersPriorityDto): Observable<any> {
    return this.baseAPI
      .put(`${APIConstants.API_UPDATE_ORDERS_PRIORITY}/${payload.shipmentId}`, { orders: payload.orders, isShipmentStarted: payload.isShipmentStarted })
      .pipe(map((res) => res.data));
  }
}
